// src/components/SignInButton.tsx
'use client';
import { useEffect, useState } from 'react';
import { getLocaleCookie } from '@/utils/localeCookiesManager.client';
import { fetchOAuthLoginUrl } from '@/features/auth/services/oauthApi';
import CWProfileDropdown from '@/context/global/components/g03/g03-d01/g03-d01-g001-cw-profile-dropdown';
import { useAuthStore } from '@/context/global/stores/authStore';

export default function CWSignInButton() {
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  const [imgError, setImgError] = useState(false);

  const { token, user } = useAuthStore();

  // user is fetched by ClientInitializer at app level
  useEffect(() => {
    if (!token || user) {
      setChecked(true);
    }
  }, [token, user]);

  const handleLogin = async () => {
    setLoading(true);
    try {
      const lang = (getLocaleCookie() as 'th' | 'en') || 'th';
      const url = await fetchOAuthLoginUrl(lang);
      window.location.href = url;
    } catch (err) {
      alert(
        'Unable to start login: ' +
          (err instanceof Error ? err.message : 'Unknown error'),
      );
    } finally {
      setLoading(false);
    }
  };

  // Always render shimmer first
  if (!checked) {
    return (
      <div className="relative">
        <div className="h-10 w-10 animate-pulse rounded-full bg-gray-200" />
      </div>
    );
  }

  // If token exists, show avatar or shimmer
  if (token) {
    const fallbackAvatar = '/assets/ic-user.svg';
    const avatarUrl = user?.avatar;
    const avatarBg = null;
    const aliasName = user?.aliasName;
    const badge = user?.badge;
    const displayAvatar = avatarUrl && !imgError ? avatarUrl : fallbackAvatar;
    return (
      <div className="relative">
        <button
          className="flex h-10 w-10 cursor-pointer items-center justify-center overflow-hidden rounded-full border border-gray-300"
          style={avatarBg ? { background: avatarBg } : { background: '#fff' }}
          onClick={() => setShowDropdown((v) => !v)}
          aria-label="User Profile"
        >
          <img
            src={displayAvatar}
            alt="User Avatar"
            className="h-full w-full object-cover"
            onError={() => setImgError(true)}
          />
        </button>
        {showDropdown && (
          <CWProfileDropdown
            avatarUrl={displayAvatar}
            avatarBg={avatarBg}
            aliasName={aliasName ?? undefined}
            badge={badge}
            onClose={() => setShowDropdown(false)}
          />
        )}
      </div>
    );
  }

  // If no token, show sign in button
  return (
    <button
      className="relative box-border flex shrink-0 cursor-pointer content-stretch items-center justify-center gap-2 rounded-[9.039px] bg-ais-green px-3 py-1.5 shadow-[-8px_0px_24.2px_0px_rgba(22,23,25,0.15)] hover:bg-ais-green/90 md:px-5 md:py-2.5"
      onClick={handleLogin}
      disabled={loading}
      type="button"
    >
      <div
        className="relative hidden size-5 shrink-0 sm:block"
        data-name="vuesax/linear/profile-circle"
        data-node-id="4249:91526"
      >
        <div className="absolute inset-0 contents">
          <img
            alt="profile"
            className="block size-full max-w-none"
            src={'/assets/ic-user.svg'}
          />
        </div>
      </div>
      <div className="relative shrink-0 text-nowrap text-[16px] font-bold not-italic leading-[0] text-[#030606] md:text-[20px]">
        <p className="whitespace-pre leading-[normal]">
          {(() => {
            const locale = getLocaleCookie();
            if (locale === 'en') return 'Sign in';
            return 'เข้าสู่ระบบ';
          })()}
        </p>
      </div>
    </button>
  );
}
